import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useUpdateAtom } from 'jotai/utils';
import { chatOpenAtom } from 'src/shared/chat';
import { useAlerts } from 'src/common/AlertManager';
import { useSchedulesQuery, useScheduleCredentialsQuery } from 'src/api/queries/schedulesQueries';
import { updateSchedule } from 'src/api/functions/schedulesApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useQueryClient } from 'react-query';
import { queryNames } from 'src/api/utils/queryNames';
import { useDeleteScheduleMutation, useRevokeCredentialsMutation } from 'src/api/mutations/schedulesMutations';
import { useAiSchedulesUnavailable } from 'src/hooks/useAiSchedulesUnavailable';
import { useWritesPaused } from 'src/hooks/useWritesPaused';
import {
  PageTitle,
  Button,
  FeatureTrialNotice,
  FeatureUnavailableBanner,
  PlanUpgradeBanner,
  WritesPausedBanner,
} from 'src/components';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { ScheduleStatusPill } from './components/ScheduleStatusPill';
import { AuthorizeBanner } from './components/AuthorizeBanner';
import { formatRecurrence, formatNextFire } from './utils';
import styles from './SchedulesList.module.scss';

type FilterTab = 'all' | 'active' | 'paused';

// Two, deliberately. A third example ran to four lines and turned a "pick one
// to get started" row into a wall of text — the long one also chained four
// operations, which reads as the required level of detail rather than as one
// of many possible shapes.
const EXAMPLE_PROMPTS: string[] = [
  'Send a summary report of all assets uploaded today.',
  'List assets that are pending approval and email the owners a reminder.',
];

export const SchedulesList: React.FC = () => {
  const history = useHistory();
  const setChatOpen = useUpdateAtom(chatOpenAtom);
  const { showSuccess, showWarning, showAlert } = useAlerts();
  const [tab, setTab] = useState<FilterTab>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const goToNewWithPrompt = (prompt: string) => {
    history.push(`/ai/schedules/new?prompt=${encodeURIComponent(prompt)}`);
  };

  const { data, isLoading, error } = useSchedulesQuery('all');
  const { data: credentials } = useScheduleCredentialsQuery();

  // Read-only "Not enabled" when the account lacks the AI addon, the probe
  // says so, the API reports the feature off, or the route is missing on the
  // old release (404) during the rollout. The page still renders all its
  // sections — only the actions are disabled — so users get a sense of the
  // feature.
  const unavailable = useAiSchedulesUnavailable(error);
  // A trial-expired (or otherwise write-blocked) account can still SEE its
  // agents, but every create / authorize / pause / edit / delete would 503
  // (#1215). Disable the actions rather than let each one fail; the list and
  // the detail page stay readable.
  const { paused: writesPaused, reason: pausedReason } = useWritesPaused();
  const writeDisabled = unavailable || writesPaused;
  // The gate can arrive two ways: as a 403 on a WRITE (create/update), or as a
  // `plan_gate` field on a successful list. The list stays readable when the
  // entitlement lapses so the customer can see their schedules are paused
  // rather than being told "No scheduled agents yet" — which reads as deleted.
  const planGate = planUpgradeRequired(error) ?? (data?.plan_gate
    ? {
        feature: data.plan_gate.feature,
        requiredPlan: data.plan_gate.required_plan,
        message: data.plan_gate.message,
        addonCode: data.plan_gate.addon_code,
        trialAvailable: Boolean(data.plan_gate.trial_available),
        trialDays: data.plan_gate.trial_days,
      }
    : null);

  const schedules = useMemo(() => {
    const list = data?.schedules || [];
    if (tab === 'all') return list;
    return list.filter((s) => s.status === tab);
  }, [data, tab]);

  // Pause/resume straight from the row. Uses the same PATCH the detail page's
  // toggle does; kept as a plain call (not a mutation hook) because the hook is
  // parameterised by a single schedule id at construction time.
  const toggleStatus = async (id: string, status: 'active' | 'paused') => {
    try {
      const result = await updateSchedule(accessToken, id, { status });
      if ('error' in result) throw result;
      showSuccess(status === 'active' ? 'Agent resumed' : 'Agent paused');
      queryClient.invalidateQueries([queryNames.schedules]);
    } catch (err: any) {
      const gate = planUpgradeRequired(err);
      showAlert(gate?.message ?? 'Could not change the agent status.');
    } finally {
      setTogglingId(null);
    }
  };

  const deleteMutation = useDeleteScheduleMutation({
    onSuccess: () => showSuccess('Agent deleted'),
  });

  const revokeMutation = useRevokeCredentialsMutation({
    onSuccess: () => showWarning('Scheduler access revoked. Future runs will fail until re-authorized.'),
  });

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Scheduled agents"
        className={styles.pageTitle}
        counter={data?.schedules?.length}
        rightContainer={
          <div className={styles.titleActions}>
            {credentials?.has_credential && (
              <Button
                size="small"
                mode="tertiary"
                disabled={writeDisabled}
                onClick={() => {
                  if (window.confirm('Revoke scheduler access? All future scheduled runs will fail until you re-authorize.')) {
                    revokeMutation.mutate();
                  }
                }}
              >
                Revoke access
              </Button>
            )}
            <Button
              size="small"
              disabled={writeDisabled}
              onClick={() => history.push('/ai/schedules/new')}
            >
              New agent
            </Button>
          </div>
        }
      />

      <div className={styles.pageContent}>
        {/* Mid-trial the list just works (the server's entitlement seam
            includes trials) — say that the access is temporary. */}
        <FeatureTrialNotice featureCode="AI_SCHEDULES" label="Scheduled agents" />
        {planGate ? <PlanUpgradeBanner gate={planGate} /> : unavailable && <FeatureUnavailableBanner />}
        {!planGate && !unavailable && writesPaused && <WritesPausedBanner reason={pausedReason} />}

        <p className={styles.overview}>
          A scheduled agent runs a prompt for you on a repeating schedule, even after you log out. Set the
          prompt once and pick how often it should run. You&apos;ll get an email if a run fails or needs your
          attention.
        </p>

        <div className={styles.examples}>
          <div className={styles.examplesHeading}>Try one of these</div>
          <ul className={styles.examplesList}>
            {EXAMPLE_PROMPTS.map((prompt) => (
              <li key={prompt}>
                <button
                  type="button"
                  className={styles.exampleItem}
                  onClick={() => goToNewWithPrompt(prompt)}
                  disabled={writeDisabled}
                  title="Use this prompt as a starting point"
                >
                  <span className={styles.exampleQuote}>&ldquo;</span>
                  <span className={styles.exampleText}>{prompt}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <AuthorizeBanner disabled={writeDisabled} />

        <div className={styles.tabs}>
          {(['all', 'active', 'paused'] as FilterTab[]).map((t) => (
            <button
              key={t}
              className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className={styles.loading}>Loading agents…</div>
        ) : schedules.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className={styles.emptyIcon}>
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="15.5" cy="16.5" r="4" stroke="currentColor" strokeWidth="1.5" />
              <path d="M15.5 15v2l1.5 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h2>No scheduled agents yet</h2>
            <p>
              Have an agent run a prompt on a repeating schedule.{' '}
              <button
                className={styles.chatLink}
                onClick={() => setChatOpen(true)}
                disabled={writeDisabled}
              >
                Try the chat
              </button>{' '}
              — say &ldquo;create a schedule that emails me a daily upload summary&rdquo;.
            </p>
            <Button
              size="small"
              disabled={writeDisabled}
              onClick={() => history.push('/ai/schedules/new')}
            >
              Create your first agent
            </Button>
          </div>
        ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Recurrence</th>
                <th>Next fire</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => {
                const nf = formatNextFire(s.next_fire_at);
                return (
                  <tr
                    key={s.id}
                    className={styles.row}
                    onClick={() => {
                      if (!unavailable) history.push(`/ai/schedules/${s.id}`);
                    }}
                  >
                    <td className={styles.titleCell}>
                      <span className={styles.titleText}>{s.title}</span>
                      {s.last_error && (
                        <span className={styles.errorHint} title={s.last_error}>
                          {s.last_error === 'invalid_grant' ? 'Re-authorize required' : s.last_error}
                        </span>
                      )}
                    </td>
                    <td>{formatRecurrence(s.recurrence)}</td>
                    <td title={nf.absolute}>{nf.relative}</td>
                    <td>
                      <ScheduleStatusPill status={s.status} lastError={s.last_error} />
                    </td>
                    <td className={styles.actionsCell}>
                      {/* Resume/Pause on the ROW, not just on the detail page.
                          The detail page had the only toggle and nothing linked
                          to it, so a customer whose trial ended — and who was
                          emailed "switch them back on from the AI Schedules
                          page" — had no way to do it. Disabled while the plan
                          gate is on: resuming is a write, so it would 403.

                          That quote is verbatim backend copy
                          (`filespin/tasks/billing_tasks.py`, catalogued in
                          `docs/reference/email-copy.md`) and still says "AI
                          Schedules", which this page no longer does. Renaming
                          it is a saas-backend change; until then the email
                          names a page the customer will not find by that
                          name. */}
                      <button
                        className={styles.actionBtn}
                        disabled={writeDisabled || !!planGate || togglingId === s.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTogglingId(s.id);
                          toggleStatus(
                            s.id,
                            s.status === 'active' ? 'paused' : 'active',
                          );
                        }}
                        title={
                          planGate
                            ? 'Upgrade to resume this agent'
                            : s.status === 'active'
                            ? 'Pause this agent'
                            : 'Resume this agent'
                        }
                      >
                        {s.status === 'active' ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        className={styles.actionBtn}
                        disabled={writeDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push(`/ai/schedules/${s.id}/edit`);
                        }}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        className={styles.actionBtn}
                        disabled={writeDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete schedule "${s.title}"?`)) {
                            deleteMutation.mutate(s.id);
                          }
                        }}
                        title="Delete"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};
